import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { API_BASE_URL, getSessionUser, hasValidSession, onSessionCleared } from './session';

// Same configuration as the pages that read Firestore: they all share this Firebase app,
// so once signed in here, their requests carry the pass checked by the Firestore rules.
const firebaseConfig = {
  apiKey: "AIzaSyDSQ0cQa7TISpd_vZWVa9dWMzbUUl-yf38",
  authDomain: "basecenterdb.firebaseapp.com",
  projectId: "basecenterdb",
  storageBucket: "basecenterdb.firebasestorage.app",
  messagingSenderId: "919766148380",
  appId: "1:919766148380:web:30db9986fa2cd8bb7106d9"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
export const db = getFirestore(app);

// Local tests only: Firebase emulators instead of the real project.
if (process.env.REACT_APP_FIREBASE_EMULATOR) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
}

// Sensitive spaces whose data lives in Firestore.
const FIRESTORE_AREAS = ['archives'];
// Ask for a new pass this long before the current one expires.
const RENEW_MARGIN_MS = 5 * 60 * 1000;
// Set when the server has no Firebase key yet: the rules are then still open.
const UNAVAILABLE_KEY = 'firebaseUnavailable';

let running = null;
let queued = null;
let renewTimer = null;
let started = Promise.resolve();

const scheduleRenewal = (until) => {
  clearTimeout(renewTimer);
  const delay = until * 1000 - Date.now() - RENEW_MARGIN_MS;
  // Last minutes of the session: the pass cannot be extended any further.
  if (delay <= 0) return;
  renewTimer = setTimeout(() => {
    if (hasValidSession()) connectFirebase().catch(() => {});
  }, delay);
};

const requestPass = async () => {
  if (sessionStorage.getItem(UNAVAILABLE_KEY)) return;
  const response = await fetch(`${API_BASE_URL}/api/firebase/token`, { method: 'POST' });
  const data = await response.json().catch(() => ({}));
  if (response.status === 503 && data.code === 'FIREBASE_NOT_CONFIGURED') {
    sessionStorage.setItem(UNAVAILABLE_KEY, '1');
    return;
  }
  if (!response.ok) throw new Error(`Laissez-passer Firebase refusé (${response.status})`);
  await signInWithCustomToken(auth, data.token);
  scheduleRenewal(data.until);
};

// Signs in to Firebase with a pass from the server (valid session required). A request
// already on its way may predate a new area grant, so one more is queued after it.
export const connectFirebase = () => {
  if (running) {
    queued =
      queued ||
      running
        .catch(() => {})
        .then(() => {
          queued = null;
          return connectFirebase();
        });
    return queued;
  }
  running = requestPass().finally(() => {
    running = null;
  });
  return running;
};

export const disconnectFirebase = () => {
  clearTimeout(renewTimer);
  sessionStorage.removeItem(UNAVAILABLE_KEY);
  return signOut(auth).catch(() => {});
};

const currentClaims = async () => (auth.currentUser ? (await auth.currentUser.getIdTokenResult()).claims : {});

// On page load: keeps the Firebase sign-in in step with the session (same account, pass
// still valid for a few minutes), otherwise renews it or signs out.
export const startFirebaseAccess = () => {
  started = (async () => {
    await auth.authStateReady();
    const sessionUser = getSessionUser();
    if (!hasValidSession() || !sessionUser) {
      if (auth.currentUser) await signOut(auth);
      return;
    }
    if (auth.currentUser && auth.currentUser.uid === sessionUser.id) {
      const { until } = await currentClaims();
      if (until && until * 1000 - Date.now() > RENEW_MARGIN_MS) {
        scheduleRenewal(until);
        return;
      }
    }
    await connectFirebase();
  })().catch(() => {});
  return started;
};

// After a sensitive space is unlocked (or on reload), makes sure the Firebase pass
// mentions it, so that its Firestore data opens too.
export const ensureFirebaseArea = async (area) => {
  if (!FIRESTORE_AREAS.includes(area)) return;
  try {
    await started;
    const { unlocked } = await currentClaims();
    if (!Array.isArray(unlocked) || !unlocked.includes(area)) await connectFirebase();
  } catch (error) {
    // Best effort: without the pass, the page reports its own loading error.
  }
};

export const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))]);

onSessionCleared(() => {
  disconnectFirebase();
});
